import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PackageCheck, Package, Calendar, Hash } from "lucide-react";

/**
 * Dialog "Scegli inventario di ritiro" — mostrato quando per un preventivo
 * esistono più pickup inventories eligibili per generare la riconsegna.
 *
 * Props:
 *   open: boolean
 *   pickups: [{ id, number, status, trip_number, pickup_date, items_count, ... }]
 *   onPick: (pickup) => void   // chiamato quando l'utente sceglie un pickup
 *   onClose: () => void
 */
export function PickupPickerDialog({ open, pickups, onPick, onClose }) {
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const p = String(iso).slice(0, 10).split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="pickup-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold font-['Outfit']">
            Scegli l'inventario di ritiro da cui generare la Riconsegna
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">
          Sono presenti più inventari di ritiro per questo preventivo che non hanno
          ancora una riconsegna in bozza. Seleziona quello da cui ereditare gli elementi.
        </p>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto py-2">
          {pickups.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p)}
              data-testid={`pickup-option-${p.id}`}
              className="w-full text-left p-3 rounded-sm border border-zinc-200 hover:border-[#002FA7] hover:bg-blue-50/40 transition group"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Package className="w-4 h-4 text-[#002FA7] shrink-0" />
                  <span className="font-mono text-sm font-semibold text-zinc-800 truncate">
                    {p.number || "—"}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">
                    {p.status || "—"}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Hash className="w-3 h-3" /> Viaggio {p.trip_number ?? "—"}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {fmtDate(p.pickup_date)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {(p.items_count ?? 0)} elementi
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-sm"
            data-testid="pickup-picker-cancel-btn"
          >
            Annulla
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
